import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createQueryKey, OWC_BASE_KEY } from '@/api/query-keys'
import { axiosInstance } from '@/api/axios'
import {
  CurrentAssetsAllowedTypes,
  CurrentAssetsBaseFieldsKeys,
  CurrentAssetsCategory,
  CurrentAssetsPeriodDataPost,
  CurrentAssetsPeriodDataResponse,
  CurrentAssetsSetup,
  CurrentAssetsSetupPostRequest,
  OwcAssetsConfigType,
  UpsertCategoryPostRequest
} from '../types/current-assets'
import {
  CurrentLiabilitiesAllowedTypes,
  CurrentLiabilitiesBaseFieldsKeys,
  CurrentLiabilitiesCategory,
  CurrentLiabilitiesPeriodDataPost,
  CurrentLiabilitiesPeriodDataResponse,
  CurrentLiabilitiesSetup,
  CurrentLiabilitiesSetupPostRequest,
  UpsertCategoryPostRequest as UpsertLiabilitiesCategoryPostRequest
} from '../types/current-liabilities'
import { queryClient } from '@/api/client'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { OwcTaxesSetup, OwcTaxesSetupPostRequest, OwcTaxesPeriodDataPost, OwcTaxesPeriodDataResponse } from '../types/owc-taxes'
import { OwcIntermediateTotalsResponse, OwcTotalsResponse } from '../types/totals'

const OWC_SETUP_KEY = createQueryKey({
  baseKey: OWC_BASE_KEY,
  keys: [`setup`]
})

export const OWC_PERIODS_KEY = createQueryKey({
  baseKey: OWC_BASE_KEY,
  keys: [`periods`]
})

export const OWC_TOTALS_KEY = createQueryKey({
  baseKey: OWC_BASE_KEY,
  keys: [`totals`]
})

// ASSETS

const getOwcCurrentAssetsSetup = async (financial_model_id: string) => {
  const response = await axiosInstance.get<CurrentAssetsSetup>(`/financial-models/${financial_model_id}/owc/assets-liabilities/assets`)
  return response.data
}

export const useGetOwcCurrentAssetsSetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: OWC_SETUP_KEY.concat([`current-assets`]),
    queryFn: () => getOwcCurrentAssetsSetup(financial_model_id)
  })

const updateOwcCurrentAssetsGeneralSetup = async (financial_model_id: string, data: CurrentAssetsSetupPostRequest) => {
  const response = await axiosInstance.put<CurrentAssetsSetup>(`/financial-models/${financial_model_id}/owc/assets-liabilities/assets/settings`, data)
  return response.data
}

export const useUpdateOwcCurrentAssetsGeneralSetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-assets`]),
    mutationFn: (data: CurrentAssetsSetupPostRequest) => updateOwcCurrentAssetsGeneralSetup(financial_model_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-assets`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-assets`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const upsertOwcCurrentAssetsCategorySetup = async (financial_model_id: string, data: UpsertCategoryPostRequest) => {
  const response = await axiosInstance.put<CurrentAssetsCategory[]>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/assets/category`,
    data
  )
  return response.data
}

export const useUpsertOwcCurrentAssetsCategorySetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-assets`]),
    mutationFn: (data: UpsertCategoryPostRequest) => upsertOwcCurrentAssetsCategorySetup(financial_model_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-assets`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-assets`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const deleteOwcCurrentAssetsCategorySetup = async (financial_model_id: string, category_id: string) => {
  const response = await axiosInstance.delete<CurrentAssetsCategory[]>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/assets/categories/${category_id}`
  )
  return response.data
}

export const useDeleteOwcCurrentAssetsCategorySetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-assets`]),
    mutationFn: (category_id: string) => deleteOwcCurrentAssetsCategorySetup(financial_model_id, category_id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-assets`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-assets`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const getOwcCurrentAssetsPeriods = async (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) => {
  const response = await axiosInstance.get<CurrentAssetsPeriodDataResponse>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/assets/entries`,
    { params: { period_type, view } }
  )
  return response.data
}
export const useGetOwcCurrentAssetsPeriods = (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: OWC_PERIODS_KEY.concat([`current-assets`, period_type, view]),
    queryFn: () => getOwcCurrentAssetsPeriods(financial_model_id, period_type, view)
  })

const updateOwcCurrentAssetsPeriodData = async (financial_model_id: string, payload: CurrentAssetsPeriodDataPost) => {
  const response = await axiosInstance.post(`/financial-models/${financial_model_id}/owc/assets-liabilities/assets/entries`, payload)
  return response.data
}

export const useUpdateOwcCurrentAssetsPeriodData = (financial_model_id: string, config_type: OwcAssetsConfigType) => {
  const mutationFn = config_type === `current-assets` ? updateOwcCurrentAssetsPeriodData : updateOwcCurrentLiabilitiesPeriodData
  return useMutation({
    mutationKey: OWC_PERIODS_KEY.concat([config_type]),
    mutationFn: (payload: CurrentAssetsPeriodDataPost) => mutationFn(financial_model_id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([config_type])
      })
    }
  })
}

export const useBulkUpdateOwcCurrentAssetsPeriodData = (financial_model_id: string, config_type: OwcAssetsConfigType) => {
  const mutationFn = config_type === `current-assets` ? updateOwcCurrentAssetsPeriodData : updateOwcCurrentLiabilitiesPeriodData
  return useMutation({
    mutationKey: OWC_PERIODS_KEY.concat([config_type]),
    mutationFn: (payload: CurrentAssetsPeriodDataPost) => mutationFn(financial_model_id, payload)
  })
}
// LIABILITIES

const getOwcCurrentLiabilitiesSetup = async (financial_model_id: string) => {
  const response = await axiosInstance.get<CurrentLiabilitiesSetup>(`/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities`)
  return response.data
}

export const useGetOwcCurrentLiabilitiesSetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: OWC_SETUP_KEY.concat([`current-liabilities`]),
    queryFn: () => getOwcCurrentLiabilitiesSetup(financial_model_id)
  })

const updateOwcCurrentLiabilitiesGeneralSetup = async (financial_model_id: string, data: CurrentLiabilitiesSetupPostRequest) => {
  const response = await axiosInstance.put<CurrentLiabilitiesSetup>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities/settings`,
    data
  )
  return response.data
}

export const useUpdateOwcCurrentLiabilitiesGeneralSetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-liabilities`]),
    mutationFn: (data: CurrentLiabilitiesSetupPostRequest) => updateOwcCurrentLiabilitiesGeneralSetup(financial_model_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-liabilities`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-liabilities`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const upsertOwcCurrentLiabilitiesCategorySetup = async (financial_model_id: string, data: UpsertLiabilitiesCategoryPostRequest) => {
  const response = await axiosInstance.put<CurrentLiabilitiesCategory[]>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities/category`,
    data
  )
  return response.data
}

export const useUpsertOwcCurrentLiabilitiesCategorySetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-liabilities`]),
    mutationFn: (data: UpsertLiabilitiesCategoryPostRequest) => upsertOwcCurrentLiabilitiesCategorySetup(financial_model_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-liabilities`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-liabilities`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const deleteOwcCurrentLiabilitiesCategorySetup = async (financial_model_id: string, category_id: string) => {
  const response = await axiosInstance.delete<CurrentLiabilitiesCategory[]>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities/categories/${category_id}`
  )
  return response.data
}

export const useDeleteOwcCurrentLiabilitiesCategorySetup = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_SETUP_KEY.concat([`current-liabilities`]),
    mutationFn: (category_id: string) => deleteOwcCurrentLiabilitiesCategorySetup(financial_model_id, category_id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`current-liabilities`])
      })
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`current-liabilities`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const getOwcCurrentLiabilitiesPeriods = async (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) => {
  const response = await axiosInstance.get<CurrentLiabilitiesPeriodDataResponse>(
    `/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities/entries`,
    { params: { period_type, view } }
  )
  return response.data
}
export const useGetOwcCurrentLiabilitiesPeriods = (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: OWC_PERIODS_KEY.concat([`current-liabilities`, period_type, view]),
    queryFn: () => getOwcCurrentLiabilitiesPeriods(financial_model_id, period_type, view)
  })

const getOwcHistoricalPeriods = (
  config_type: OwcAssetsConfigType,
  financial_model_id: string,
  view: PeriodTypeView
): Promise<CurrentAssetsPeriodDataResponse | CurrentLiabilitiesPeriodDataResponse> =>
  config_type === `current-assets`
    ? getOwcCurrentAssetsPeriods(financial_model_id, `historical`, view)
    : getOwcCurrentLiabilitiesPeriods(financial_model_id, `historical`, view)

export const useGetOwcHistoricalPeriods = (config_type: OwcAssetsConfigType, financial_model_id: string, view: PeriodTypeView) =>
  useQuery<CurrentAssetsPeriodDataResponse | CurrentLiabilitiesPeriodDataResponse>({
    queryKey: OWC_PERIODS_KEY.concat([config_type, `historical`, view]),
    queryFn: () => getOwcHistoricalPeriods(config_type, financial_model_id, view),
    enabled: Boolean(financial_model_id)
  })

const updateOwcCurrentLiabilitiesPeriodData = async (financial_model_id: string, payload: CurrentLiabilitiesPeriodDataPost) => {
  const response = await axiosInstance.post(`/financial-models/${financial_model_id}/owc/assets-liabilities/liabilities/entries`, payload)
  return response.data
}

// TAXES

const getOwcTaxesSetup = async (financial_model_id: string) => {
  const response = await axiosInstance.get<OwcTaxesSetup>(`/financial-models/${financial_model_id}/owc/receivables/`)
  return response.data
}

export const useGetOwcTaxesSetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: OWC_SETUP_KEY.concat([`taxes`]),
    queryFn: () => getOwcTaxesSetup(financial_model_id)
  })

const updateOwcTaxesSetup = async (financial_model_id: string, data: OwcTaxesSetupPostRequest) => {
  const response = await axiosInstance.put<OwcTaxesSetup>(`/financial-models/${financial_model_id}/owc/receivables/settings`, data)
  return response.data
}

export const useUpdateOwcTaxesSetup = (financial_model_id: string) =>
  useMutation({
    mutationFn: (data: OwcTaxesSetupPostRequest) => updateOwcTaxesSetup(financial_model_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_SETUP_KEY.concat([`taxes`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

const getOwcTaxesPeriods = async (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) => {
  const response = await axiosInstance.get<OwcTaxesPeriodDataResponse>(`/financial-models/${financial_model_id}/owc/receivables/entries`, {
    params: { period_type, view }
  })
  return response.data
}

export const useGetOwcTaxesPeriods = (financial_model_id: string, period_type: PeriodTypeQuery, view: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: OWC_PERIODS_KEY.concat([`taxes`, period_type, view]),
    queryFn: () => getOwcTaxesPeriods(financial_model_id, period_type, view)
  })

const updateOwcTaxesPeriodData = async (financial_model_id: string, payload: OwcTaxesPeriodDataPost) => {
  const response = await axiosInstance.post(`/financial-models/${financial_model_id}/owc/receivables/entries`, payload)
  return response.data
}

export const useUpdateOwcTaxesPeriodData = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_PERIODS_KEY.concat([`taxes`]),
    mutationFn: (payload: OwcTaxesPeriodDataPost) => updateOwcTaxesPeriodData(financial_model_id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([`taxes`])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    }
  })

export const useBulkUpdateOwcTaxesPeriodData = (financial_model_id: string) =>
  useMutation({
    mutationKey: OWC_PERIODS_KEY.concat([`taxes`]),
    mutationFn: (payload: OwcTaxesPeriodDataPost) => updateOwcTaxesPeriodData(financial_model_id, payload)
  })

type SwitchProvisionColumnTypeParams = {
  financial_model_id: string
  input_type: CurrentLiabilitiesAllowedTypes | CurrentAssetsAllowedTypes
  column_name: CurrentLiabilitiesBaseFieldsKeys | CurrentAssetsBaseFieldsKeys
  month?: number
  year?: number
  category_id?: string | null
}

type SwitchOwcColumnTypeParams = {
  config_type: OwcAssetsConfigType
  params: SwitchProvisionColumnTypeParams
}

const switchOwcColumnType = async (params: SwitchOwcColumnTypeParams) => {
  const { financial_model_id, column_name, ...rest } = params.params
  const urlType = params.config_type === `current-assets` ? `assets` : `liabilities`

  const response = await axiosInstance.post(`/financial-models/${financial_model_id}/owc/${urlType}/config/${column_name}`, undefined, {
    params: rest
  })
  return response.data
}

export const useSwitchOwcColumnType = () =>
  useMutation({
    mutationFn: (params: SwitchOwcColumnTypeParams) => switchOwcColumnType(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY
      })
    }
  })

// TOTALS

type TotalsParams = {
  financial_model_id: string
  config_type: OwcAssetsConfigType
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getOwcIntermediateTotals = async (params: TotalsParams) => {
  const { financial_model_id, type, view } = params
  const response = await axiosInstance.get<OwcIntermediateTotalsResponse>(`/financial-models/${financial_model_id}/owc/intermediary`, {
    params: { type, view }
  })
  return response.data
}

export const useGetOwcIntermediateTotals = (params: TotalsParams) => {
  const { config_type, type, view } = params
  const objectKey = config_type === `current-assets` ? `assets` : `liabilities`
  return useSuspenseQuery({
    queryKey: OWC_TOTALS_KEY.concat([config_type, type, view]),
    queryFn: () => getOwcIntermediateTotals(params),
    select: data => data?.[objectKey],
    staleTime: 0
  })
}
const getOwcTotals = async (params: Omit<TotalsParams, `config_type`>) => {
  const { financial_model_id, type, view } = params
  const response = await axiosInstance.get<OwcTotalsResponse>(`/financial-models/${financial_model_id}/owc/totals`, { params: { type, view } })
  return response.data
}

export const useGetOwcTotals = (params: Omit<TotalsParams, `config_type`>) =>
  useSuspenseQuery({
    queryKey: OWC_TOTALS_KEY.concat([params.type, params.view]),
    queryFn: () => getOwcTotals(params),
    staleTime: 0
  })
